
import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, Database, CheckCircle } from "lucide-react";
import { MedicalKnowledgeService } from "@/services/medicalKnowledge";
import { useToast } from "@/hooks/use-toast";

export const DatabaseInitializer: React.FC = () => {
  const [isInitializing, setIsInitializing] = useState(false);
  const [progress, setProgress] = useState<string>('');
  const [completed, setCompleted] = useState(false);
  const { toast } = useToast();

  const initializeDatabase = async () => {
    setIsInitializing(true);
    setProgress('Starting database initialization...');
    
    try {
      // Trigger PubMed sync
      setProgress('Syncing PubMed medical research data...');
      const pubmedSuccess = await MedicalKnowledgeService.triggerSync('pubmed');
      
      if (pubmedSuccess) {
        toast({
          title: "PubMed Sync Started",
          description: "Medical research data sync initiated",
        });
      }

      // Wait a bit before starting next sync
      await new Promise(resolve => setTimeout(resolve, 2000));

      // Trigger TRIP Database sync
      setProgress('Syncing TRIP Database clinical guidelines...');
      const tripSuccess = await MedicalKnowledgeService.triggerSync('trip');
      
      if (tripSuccess) {
        toast({
          title: "TRIP Database Sync Started",
          description: "Clinical guidelines sync initiated",
        });
      }

      // Wait a bit before starting next sync
      await new Promise(resolve => setTimeout(resolve, 2000));

      // Trigger OpenFDA sync
      setProgress('Syncing OpenFDA drug safety data...');
      const fdaSuccess = await MedicalKnowledgeService.triggerSync('openfda');
      
      if (fdaSuccess) {
        toast({
          title: "OpenFDA Sync Started",
          description: "Drug safety data sync initiated",
        });
      }

      // Wait a bit before starting next sync
      await new Promise(resolve => setTimeout(resolve, 2000));

      // Trigger DailyMed sync
      setProgress('Syncing DailyMed drug information...');
      const dailymedSuccess = await MedicalKnowledgeService.triggerSync('dailymed');
      
      if (dailymedSuccess) {
        toast({
          title: "DailyMed Sync Started",
          description: "FDA drug labeling data sync initiated",
        });
      }

      // Wait a bit before starting next sync
      await new Promise(resolve => setTimeout(resolve, 2000));

      // Trigger ClinicalTrials sync
      setProgress('Syncing ClinicalTrials.gov research data...');
      const clinicalTrialsSuccess = await MedicalKnowledgeService.triggerSync('clinicaltrials');
      
      if (clinicalTrialsSuccess) {
        toast({
          title: "ClinicalTrials.gov Sync Started",
          description: "Clinical research data sync initiated",
        });
      }

      if (pubmedSuccess && tripSuccess && fdaSuccess && dailymedSuccess && clinicalTrialsSuccess) {
        setProgress('All sync processes initiated successfully!');
        setCompleted(true);
        toast({
          title: "Database Initialization Complete",
          description: "All 5 medical data sources are being synchronized",
        });
      } else {
        throw new Error('Some sync processes failed to start');
      }

    } catch (error) {
      console.error('Database initialization error:', error);
      setProgress('Initialization failed - please try again');
      toast({
        title: "Initialization Failed",
        description: "Failed to start database synchronization",
        variant: "destructive"
      });
    } finally {
      setIsInitializing(false);
    }
  };

  return (
    <Card className="max-w-2xl mx-auto">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Database className="h-6 w-6" />
          Initialize Medical Database
        </CardTitle>
        <CardDescription>
          Pre-populate the medical knowledge base with data from PubMed, TRIP Database, OpenFDA, DailyMed, and ClinicalTrials.gov
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {progress && (
          <div className="p-4 bg-blue-50 rounded-lg">
            <p className="text-sm text-blue-800">{progress}</p>
          </div>
        )}
        
        <Button
          onClick={initializeDatabase}
          disabled={isInitializing || completed}
          className="w-full"
          size="lg"
        >
          {isInitializing ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Initializing Database...
            </>
          ) : completed ? (
            <>
              <CheckCircle className="h-4 w-4 mr-2" />
              Database Initialized
            </>
          ) : (
            <>
              <Database className="h-4 w-4 mr-2" />
              Start Database Initialization
            </>
          )}
        </Button>
        
        {completed && (
          <div className="text-sm text-gray-600">
            <p>The database is now being populated with medical data from 5 sources. This process may take several minutes to complete.</p>
            <p className="mt-2">You can monitor the progress in the Medical Data Sync section.</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
